<template>
    <EditComponent
        dialog-id="editAboutDialog"
        open-dialog-event="about:edit-mode"
        updated-event="about:updated"
        success-message-key="profile.section.about.edit.success"
        size="default"
        confirm-data-testid="profile-edition-section-confirm-button"
        :confirm-function="update"
        @close="resetValues"
    >
        <template #edit-header>
            <p>{{ $t('profile.section.about.edit.title') }}</p>
        </template>
        <template #edit-body>
            <VJoyWrapper
                class="profile-about-edition-section__wrapper"
                data-testid="profile-about-section__edit-description"
                no-margin
                align="stretch"
                row-gap="8"
            >
                <VJoyWrapper class="profile-about-edition-section__wrapper" no-margin direction="column" align="stretch" row-gap="2">
                    <VJoyLabel :text="t('profile.section.about.subtitle.description')" />
                    <EditDescription />
                </VJoyWrapper>
                <VJoyWrapper
                    class="profile-about-edition-section__wrapper"
                    data-testid="profile-about-section__edit-industry-expertises"
                    no-margin
                    direction="column"
                    align="stretch"
                    row-gap="2"
                >
                    <VJoyLabel :text="t('profile.section.about.subtitle.industry.expertises')">
                        <template #label-legend>
                            {{ t('profile.section.industry.expertises.edit.legend', [industryExpertisesCount.max]) }}
                        </template>
                    </VJoyLabel>
                    <EditIndustryExpertises />
                </VJoyWrapper>
                <VJoyWrapper
                    class="profile-about-edition-section__wrapper"
                    data-testid="profile-about-section__edit-languages"
                    no-margin
                    direction="column"
                    align="stretch"
                    row-gap="2"
                >
                    <VJoyLabel :text="t('profile.section.about.subtitle.languages')" />
                    <EditLanguages />
                </VJoyWrapper>
                <VJoyWrapper
                    class="profile-about-edition-section__wrapper"
                    data-testid="profile-about-section__edit-workplace-prefs"
                    no-margin
                    direction="column"
                    align="stretch"
                    row-gap="2"
                >
                    <VJoyLabel :text="t('profile.section.about.subtitle.workplace.preferences')" />
                    <EditWorkplacePreferences :has-invalid-alternative-addresses="hasInvalidAlternativeAddresses" />
                </VJoyWrapper>
            </VJoyWrapper>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {validateForm} from '@malt/forms';
    import {VJoyLabel, VJoyWrapper} from '@maltjoy/core-vue';
    import {ProfileEditionFunctionalErrorErrorCodeEnum} from '@api';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {useProfileAbout} from '~/features/about/composable/useProfileAbout';
    import {
        type AlternativeAddressEditionItem,
        type IndustryExpertiseEditionItem,
        type LanguageEditionItem,
        type TravelLocationEditionItem,
        useCollectScopeRecord,
    } from '~/features/about/types/EditAboutSection';
    import EditDescription from '~/features/about/ui/edit/description/EditDescription.vue';
    import EditIndustryExpertises from '~/features/about/ui/edit/industry-expertises/EditIndustryExpertises.vue';
    import EditLanguages from '~/features/about/ui/edit/languages/EditLanguages.vue';
    import EditWorkplacePreferences from '~/features/about/ui/edit/workplace-preferences/EditWorkplacePreferences.vue';
    import {useRulesStore} from '~/store/rules/rulesStore';

    const {t} = useTranslation();
    const {updateProfileAbout} = useProfileAbout();

    const {industryExpertisesCount} = storeToRefs(useRulesStore());

    const {r$} = useCollectScopeRecord<{
        descriptionEdition: {
            description: string;
        };
        industryExpertisesEdition: {
            industryExpertises: IndustryExpertiseEditionItem[];
        };
        languagesEdition: {
            languages: LanguageEditionItem[];
        };
        workplacePreferencesEdition: {
            isRemoteOnly: string;
            isWillingToTravel: string;
            principalAddressRadius: number;
            alternativeAddresses: AlternativeAddressEditionItem[];
            travelLocations: TravelLocationEditionItem[];
        };
    }>();

    async function update() {
        const {valid} = await validateForm(r$);
        if (!valid) {
            return false;
        }

        try {
            await updateProfileAbout({
                description: r$.$value.descriptionEdition.description,
                industryExpertises: r$.$value.industryExpertisesEdition.industryExpertises.map((item) => item.humanId),
                languages: r$.$value.languagesEdition.languages.map((item) => ({
                    code: item.code,
                    level: item.level,
                })),
                workplacePreferences: {
                    isRemoteOnly: r$.$value.workplacePreferencesEdition.isRemoteOnly === 'true',
                    isWillingToTravel: r$.$value.workplacePreferencesEdition.isWillingToTravel === 'true',
                    principalWorkLocationSearchRadius: r$.$value.workplacePreferencesEdition.principalAddressRadius,
                    alternativeWorkLocations:
                        r$.$value.workplacePreferencesEdition.isRemoteOnly === 'false'
                            ? r$.$value.workplacePreferencesEdition.alternativeAddresses.map((item) => ({
                                  location: {
                                      ...item.address,
                                  },
                                  radius: item.radius,
                              }))
                            : [],
                    travelLocations:
                        r$.$value.workplacePreferencesEdition.isWillingToTravel === 'true'
                            ? r$.$value.workplacePreferencesEdition.travelLocations.map((item) => item.value)
                            : [],
                },
            });
            resetValues();
        } catch (err: any) {
            const errorCode = err?.cause?.response?._data?.errorCode;
            if (errorCode === ProfileEditionFunctionalErrorErrorCodeEnum.InvalidAlternativeWorkplaces) {
                hasInvalidAlternativeAddresses.value = true;
                await validateForm(r$);
            }
            return false;
        }
        resetValues();
    }

    const hasInvalidAlternativeAddresses = ref<boolean>(false);
    watch(
        () => r$.$value?.workplacePreferencesEdition?.alternativeAddresses,
        () => {
            hasInvalidAlternativeAddresses.value = false;
        },
        {deep: true},
    );

    function resetValues() {
        r$.$reset({toOriginalState: true});
    }
</script>
<style lang="scss" scoped>
    .profile-about-edition-section {
        &__title {
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
        }

        &__wrapper {
            width: 100%;
            margin-right: var(--joy-core-spacing-2); // to leave a space for scrolling
        }
    }
</style>
